export type ActionType =
  | "contact_attempt"
  | "view"
  | "comment"
  | "like"
  | "favorite"
  | "share"
  | "search"
  | "publish"
  | "login"
  | "admin_login"
  | "logout"
  | "create"
  | "update"
  | "delete"
  | "moderate"
  | "export"
  | "account_delete"
  | "register"
  | "verify_email"
  | "password_reset_request"
  | "password_reset"
  | "password_change"
  | "totp_setup"
  | "totp_verify"
  | "user_logout"
  | "comment_update"
  | "comment_delete"
  | "review_create";

export type EntityType =
  | "ad"
  | "ads"
  | "appeal"
  | "appeals"
  | "chat"
  | "chats"
  | "comment"
  | "comments"
  | "option"
  | "broadcast"
  | "catalog"
  | "category"
  | "complaint"
  | "complaints"
  | "faq"
  | "moderator"
  | "moderators"
  | "notification"
  | "notifications"
  | "post"
  | "posts"
  | "profile"
  | "profiles"
  | "proposal"
  | "proposals"
  | "question"
  | "questions"
  | "review"
  | "reviews"
  | "seller"
  | "settings"
  | "system"
  | "topic"
  | "topics"
  | "user"
  | "users";

export type ActionStatus = "success" | "failed" | "pending";

export type LogOrderingField = "created_at" | "user_id" | "action_type" | "status" | "entity_type";
export type SortDirection = LogOrderingField | `-${LogOrderingField}`;

export interface UserActionUserResponse {
  id: string;
  username: string | null;
  number: number | null;
  phone: string | null;
  avatar: string | null;
}

export interface UserActionEntityResponse {
  id: string;
  title?: string | null;
  number?: number | null;
}

export interface UserActionResponse {
  id: string;
  createdAt: string;
  userId: string | null;
  actionType: ActionType;
  entityId: string | null;
  entityType: EntityType;
  targetEntityId: string | null;
  targetEntityType: EntityType | null;
  user: UserActionUserResponse | null;
  entity: UserActionEntityResponse | null;
  targetEntity: UserActionEntityResponse | null;
  description: string | null;
  status: ActionStatus | null;
  appScreen: string | null;
  version: string | null;
  platform: string | null;
  vendor: string | null;
  model: string | null;
  build: string | null;
  clientVersion: string | null;
  metadata: LogMetadataResponse;
}

export interface UserActionsRequest {
  action_type?: ActionType;
  app_screen?: string;
  client_version?: string;
  created_from?: string;
  created_to?: string;
  entity_id?: string;
  entity_type?: EntityType;
  limit?: number;
  offset?: number;
  ordering?: SortDirection;
  search?: string;
  status?: ActionStatus;
  user_id?: string;
}

export interface UserActionsListResponse {
  list: UserActionResponse[];
  totalCount: string | number;
}

export interface LogMetadataResponse {
  search?: string;
  feedType?: any;
  categoryId?: string;
  catalogId?: string;
  category?: ShortMetadataObjectResponse;
  catalog?: ShortMetadataObjectResponse;
}

export interface ShortMetadataObjectResponse {
  id: string;
  title: string;
}

export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  contact_attempt: "Попытка контакта",
  view: "Просмотр",
  comment: "Комментарий",
  like: "Лайк",
  favorite: "В избранное",
  share: "Поделиться",
  search: "Поиск",
  publish: "Публикация объявления",
  login: "Вход пользователя",
  admin_login: "Вход администратора",
  logout: "Выход администратора",
  create: "Создание",
  update: "Обновление",
  delete: "Удаление",
  moderate: "Модерация",
  export: "Экспорт",
  account_delete: "Удаление своего аккаунта",
  register: "Регистрация",
  verify_email: "Подтверждение почты",
  password_reset_request: "Запрос восстановления пароля",
  password_reset: "Сброс пароля",
  password_change: "Смена пароля",
  totp_setup: "Настройка двухфакторной аутентификации",
  totp_verify: "Проверка двухфакторной аутентификации",
  user_logout: "Выход пользователя",
  comment_update: "Изменение комментария",
  comment_delete: "Удаление комментария",
  review_create: "Создание отзыва",
};

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  ad: "Объявление",
  ads: "Объявления",
  appeal: "Обращение",
  appeals: "Обращения",
  chat: "Чат",
  chats: "Чаты",
  comment: "Комментарий",
  comments: "Комментарии",
  option: "Опция",
  broadcast: "Рассылка",
  catalog: "Раздел/подраздел",
  category: "Раздел",
  complaint: "Жалоба",
  complaints: "Жалобы",
  faq: "FAQ",
  moderator: "Модератор",
  moderators: "Модераторы",
  notification: "Уведомление",
  notifications: "Уведомления",
  post: "Пост",
  posts: "Посты",
  profile: "Профиль",
  profiles: "Профили",
  proposal: "Предложение",
  proposals: "Предложения",
  question: "Вопрос",
  questions: "Вопросы",
  review: "Отзыв",
  reviews: "Отзывы",
  settings: "Настройки",
  system: "Система",
  topic: "Тема",
  topics: "Темы",
  user: "Пользователь",
  users: "Пользователи",
  seller: "Профиль",
};

export const STATUS_LABELS: Record<ActionStatus, string> = {
  success: "Успешно",
  failed: "Ошибка",
  pending: "В ожидании",
};

export type LogsTabsType = "users" | "moderators";

export const getEntityName = (log: UserActionResponse) => {
  switch (log.actionType) {
    case "export":
      switch (log.entityType) {
        case "ad":
          return "Список объявлений";
        case "user":
        case "profile":
          return "Список пользователей";
        case "comment":
          return "Список комментариев";
        case "chat":
          return "Список чатов";
        case "review":
          return "Список отзывов";
        case "post":
          return "Список постов";
        case "topic":
          return "Список тем";
        case "moderator":
          return "Список модераторов";
        case "settings":
        case "system":
          return "Экспорт";
        default:
          return ENTITY_TYPE_LABELS[log.entityType];
      }
    case "search":
      return log.metadata?.search ?? "";
    default:
      return ENTITY_TYPE_LABELS[log.entityType];
  }
};

export function isMany(type: EntityType): boolean {
  const manyExceptions: EntityType[] = ["settings", "system"];
  return type.endsWith("s") && !manyExceptions.includes(type);
}
